import { DiscoveryProductsRecentlyAddedResponseType } from "@sellar-io/api-v2/types";

import { twMerge } from "tailwind-merge";
import { Icon } from "../../../components";
import { DietaryDisplayList } from "../../../components/dietaryDisplayList/DietaryDisplayList";
import { ParentLink } from "../../../components/navigation/ParentLink";

import { CardImage } from "@sellar-io/components";

import { ImageSlider } from "../../../components/imageSlider/ImageSlider";
import { useMediaQuery } from "usehooks-ts";
import { ORDER_BUYER } from "../../../constants/routes";
import { PreOrderPill } from "./components/DiscoveryPreOrderProductCard";

export interface DiscoveryListProductCardProps extends Pick<
  DiscoveryProductsRecentlyAddedResponseType[number],
  | "ProductImages"
  | "heroImageUrl"
  | "name"
  | "vegan"
  | "glutenFree"
  | "lactoseFree"
  | "kind"
  | "abv"
  | "containerTypes"
  | "id"
  | "imageUrl"
  | "Supplier"
> {
  preOrderAvailableFromDates?: Date[];
}

export const DiscoveryListProductCard = ({
  product,
  className,
  containerClassName,
  onClick,
}: {
  product: DiscoveryListProductCardProps;
  className?: string;
  containerClassName?: string;
  onClick: () => void;
}) => {
  let productHeroImages = product.ProductImages.filter(
    (ProductImage) => !!ProductImage.url,
  ).map((ProductImage) => ProductImage.url);

  if (productHeroImages.length === 0 && product.heroImageUrl)
    productHeroImages = [product.heroImageUrl];

  const hasProductHeroImages =
    productHeroImages && productHeroImages.length > 0;

  const isDesktop = useMediaQuery("(min-width: 770px)");

  const preOrderAvailableFromDates: Date[] =
    product.preOrderAvailableFromDates || [];

  const productAbv = product.abv ? `${product.abv}%` : "";

  return (
    <div
      className={twMerge(
        "flex w-50 min-w-50 flex-col gap-4",
        containerClassName,
      )}
    >
      <ParentLink
        to={ORDER_BUYER.href({
          supplierIdOrUrlId: product.Supplier?.urlId ?? "undefined",
        })}
        className="flex flex-row items-center gap-1"
      >
        <img
          src={product.Supplier?.Business?.logoUrl ?? ""}
          alt={product.Supplier?.Business?.name}
          height="20"
          width="20"
          className="shrink-0 rounded-full object-cover"
        />
        <p
          className={
            "overflow-hidden text-sm font-semibold text-nowrap text-ellipsis text-zinc-800"
          }
        >
          {product.Supplier?.Business?.name}
        </p>
      </ParentLink>
      <div
        key={product.id}
        className={twMerge("flex cursor-pointer flex-col gap-3", className)}
        onClick={onClick}
      >
        <div
          className={twMerge(
            "relative h-30 min-w-50 cursor-pointer overflow-clip rounded-lg bg-white",
          )}
        >
          {hasProductHeroImages ? (
            <ImageSlider
              images={productHeroImages}
              hoverAnimate={true}
              imageClassName="pt-[120px]"
              swipe={isDesktop}
            />
          ) : product.heroImageUrl || !!productHeroImages[0] ? (
            <CardImage
              imageUrl={product.heroImageUrl ?? productHeroImages[0] ?? ""}
              fallback="product-hero"
              className={twMerge("h-30 min-w-50 rounded-lg")}
            />
          ) : (
            <div
              className={twMerge(
                "relative h-30 min-w-50 cursor-pointer overflow-clip rounded-lg bg-white",
              )}
            >
              <CardImage
                imageUrl={product.imageUrl ?? product.Supplier?.imageUrl ?? ""}
                fallback="product-hero"
                className={twMerge("h-30 min-w-62.5 blur-lg")}
              />
              <div
                className={
                  "absolute inset-0 flex items-center justify-center text-white"
                }
              >
                <img
                  src={
                    product.imageUrl ??
                    product.Supplier?.Business?.logoUrl ??
                    ""
                  }
                  alt={product.Supplier?.Business?.name}
                  height="100"
                  width="100"
                  className="rounded-full"
                />
              </div>
            </div>
          )}
          {preOrderAvailableFromDates.length > 0 ? (
            <PreOrderPill availableFromDates={preOrderAvailableFromDates} />
          ) : (
            <div className="absolute top-[8px] right-[10px] inline-flex h-[32px] w-[32px] items-center justify-start gap-2.5 rounded-[100px] border border-zinc-300 bg-white p-2">
              <Icon icon={"PLUS"} size={0.66} />
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <div className={"text-sm font-semibold text-zinc-800"}>
            {product.name}{" "}
            <DietaryDisplayList
              dietaryRequirements={{
                VG: Boolean(product.vegan),
                GF: Boolean(product.glutenFree),
                LF: Boolean(product.lactoseFree),
              }}
              mode="text"
            />
          </div>
          <p className={"text-xs text-zinc-500"}>
            {[product.kind, productAbv].filter(Boolean).join(" // ")}
          </p>
        </div>
        <p className={"text-xs font-bold text-zinc-500"}>
          {" "}
          {product.containerTypes.map((type) => type.toUpperCase()).join(" • ")}
        </p>
      </div>
    </div>
  );
};
